import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAgentRulesStore } from "@/store/agentRulesStore";

interface AgentRuleFormData {
  content: string;
}

const getInitialFormData = (): AgentRuleFormData => ({
  content: "",
});

/**
 * Props for the AgentRuleEditorSheet component.
 * Supports two modes:
 * - "create": For creating new rules (no ruleId needed)
 * - "edit": For editing existing rules (requires ruleId)
 */
type AgentRuleEditorSheetProps =
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      ruleId?: undefined;
      onSave: (content: string) => Promise<boolean>;
      mode: "create";
    }
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      ruleId: string;
      onSave: (content: string) => Promise<boolean>;
      mode: "edit";
    };

/**
 * A sheet component for creating or editing agent rules/shop rules.
 * In edit mode, pulls the rule data from the store.
 * Provides a textarea for editing the shop rule content.
 */
export default function AgentRuleEditorSheet({
  open,
  onOpenChange,
  ruleId,
  onSave,
  mode,
}: AgentRuleEditorSheetProps) {
  const getRule = useAgentRulesStore((state) => state.getRule);

  const rule = mode === "edit" && ruleId ? getRule(ruleId) : undefined;

  const [formData, setFormData] =
    useState<AgentRuleFormData>(getInitialFormData());
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && mode === "edit" && rule) {
      setFormData({ content: rule.content });
    } else if (open && mode === "create") {
      setFormData(getInitialFormData());
    }
  }, [open, mode, rule]);

  const handleSave = async () => {
    if (!formData.content.trim()) {
      toast.error("Shop rule content cannot be empty");
      return;
    }

    setIsSaving(true);

    const success = await onSave(formData.content);

    setIsSaving(false);

    if (success) {
      toast.success(
        mode === "create"
          ? "Shop rule created successfully"
          : "Shop rule updated successfully",
      );
      onOpenChange(false);
    } else {
      toast.error(
        mode === "create"
          ? "Failed to create shop rule"
          : "Failed to update shop rule",
      );
    }
  };

  const hasChanges =
    mode === "create"
      ? formData.content.trim() !== ""
      : rule && formData.content !== rule.content;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>
            {mode === "create" ? "Create Shop Rule" : "Edit Shop Rule"}
          </SheetTitle>
          <SheetDescription>
            {mode === "create"
              ? "Add a new shop rule for the agent to follow."
              : "Update the shop rule content."}
          </SheetDescription>
        </SheetHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="content">Shop Rule Content</Label>
            <Textarea
              id="content"
              placeholder="Enter shop rule content..."
              value={formData.content}
              onChange={(e) =>
                setFormData({ ...formData, content: e.target.value })
              }
              className="overflow-hidden"
              style={{ minHeight: "100px" }}
              disabled={isSaving}
            />
          </div>
        </div>

        <SheetFooter>
          <SheetClose asChild>
            <Button variant="outline" disabled={isSaving}>
              Cancel
            </Button>
          </SheetClose>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving || !formData.content.trim()}
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSaving
              ? mode === "create"
                ? "Creating..."
                : "Saving..."
              : mode === "create"
                ? "Create"
                : "Save"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
